import React, { useEffect, useRef, useState } from "react";
import { Input } from "../../../../../shadcn/components/ui/input";
import { Button } from "../../../../../shadcn/components/ui/button";
import { BetaForm as useBetaForm } from "@reusejs/react";
import {
  fetchFuzzyMatchDetails,
  fetchIDCardDetails,
  fetchIDCardOCRDetails,
  getBidderByUuid,
  maskUploadedAadhaar,
  updateBidderForEvent,
} from "services/bidder";
import { getFileByUuid } from "services";
import CustomMultipleFileUploader from "../../../../banker/bidder/CustomMultipleFileUploader";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../shadcn/components/ui/dialog";
import { Label } from "../../../../../shadcn/components/ui/label";
import ExclamationIcon from "../../../../../icons/ExclamationIcon";

const isDigioPanFullVerificationEnabled =
  process.env.NEXT_PUBLIC_DIGIO_PAN_FULL_VERIFICATION_ENABLED === "true";

interface AssignRepresentativeComponentV2Props {
  stepsTitles: any;
  activeStep: any;
  setActiveStep: any;
  bidderUuid: any;
  init: any;
}
export default function AssignRepresentativeComponentV2({
  bidderUuid,
  init,
  stepsTitles,
  activeStep,
  setActiveStep,
}: AssignRepresentativeComponentV2Props) {
  const [loading, setLoading] = useState(false);
  const [isFetchingPan, setIsFetchingPan] = useState(false);
  const [isNameVerified, setIsNameVerified] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [kycFiles, setKycFiles] = useState<any>([]);
  const [renderKey, setRenderKey] = useState(0);

  // V4 name-first flow: debounce ref for PAN verification trigger
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [stage, setStage] = useState<any>(null);
  const form = useBetaForm({
    mobile: "",
    email: "",
    pan: "",
    name: "",
    designation: "",
    ar_authorization_letter: "",
  });
  const mobileNumberRegEx: RegExp = /^[0-9]{10}$/;
  const emailRegEx: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const panRegex: RegExp = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  const form_constraints = {
    mobile: {
      presence: {
        allowEmpty: false,
      },
      format: {
        pattern: mobileNumberRegEx,
        message: "^Mobile number is not valid.",
      },
      length: {
        maximum: 10,
        message: "^Mobile number is too long (maximum is 10 characters)",
      },
    },
    email: {
      presence: {
        allowEmpty: false,
      },
      format: {
        pattern: emailRegEx,
        message: "^Email is not valid.",
      },
    },
    pan: {
      presence: {
        allowEmpty: false,
      },
      length: {
        maximum: 10,
      },
      format: {
        pattern: panRegex,
      },
    },
    name: {
      presence: {
        allowEmpty: true,
      },
    },
    designation: {
      presence: {
        allowEmpty: false,
      },
    },
    ar_authorization_letter: {
      presence: {
        allowEmpty: false,
      },
    },
  };

  // V4 name-first flow: Debounced function to trigger PAN verification when name >= 3 chars
  const triggerPanVerification = () => {
    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer
    debounceTimerRef.current = setTimeout(() => {
      const currentPan = form.getField("pan") || "";
      const currentName = (form.getField("name") || "").toString().trim();

      // Only trigger if name >= 3 AND PAN has value
      if (
        currentName.length >= 3 &&
        currentPan.length === 10 &&
        panRegex.test(currentPan)
      ) {
        handlePanVerification(
          currentPan,
          form,
          setIsNameVerified,
          setIsFetchingPan,
          setLoadingText,
        );
      }
    }, 500);
  };

  // Internal PAN verification function - called from debounced trigger or direct onChange
  const handlePanVerification = async (
    panValue: string,
    formInstance: any,
    setIsNameVerifiedFn: (value: boolean) => void,
    setIsFetchingPanFn: (value: boolean) => void,
    setLoadingTextFn: (text: string) => void,
  ) => {
    try {
      setIsFetchingPanFn(true);
      setLoadingTextFn("Fetching PAN details. Please wait...");

      const payload: any = {
        card_type: "PAN",
        card_number: panValue,
      };

      // Add name if full verification is enabled AND name has at least 3 chars
      const userName = (formInstance.getField("name") || "").toString().trim();
      if (isDigioPanFullVerificationEnabled && userName.length >= 3) {
        payload.name = userName.toUpperCase();
      }

      const res: any = await fetchIDCardDetails(payload);
      const panCategory = (res?.category || "").toString().toLowerCase();
      const panStatus = (res?.status || "").toString().toLowerCase();
      const nameAsPerPanMatch = res?.name_as_per_pan_match;
      const userEnteredName = (formInstance.getField("name") || "")
        .toString()
        .trim();

      // Check if PAN status is valid first (independent of name)
      const isPanStatusValid = panStatus === "valid";

      // Only check name_as_per_pan_match if name has at least 3 characters
      const isNameLongEnough = userEnteredName.length >= 3;
      const isNameMatch = isDigioPanFullVerificationEnabled
        ? isNameLongEnough && nameAsPerPanMatch === true
        : true;

      if (panCategory !== "individual") {
        formInstance.pushError({
          pan: ["Representative PAN must be an individual PAN."],
        });
        if (!isDigioPanFullVerificationEnabled) {
          formInstance.setField("name", "");
        }
        setIsNameVerifiedFn(false);
        setIsFetchingPanFn(false);
        setLoadingTextFn("");
        return;
      }

      if (!isPanStatusValid) {
        formInstance.pushError({
          pan: ["PAN seems invalid. Please check the number and try again."],
        });
        if (!isDigioPanFullVerificationEnabled) {
          formInstance.setField("name", "");
        }
        setIsNameVerifiedFn(false);
        setIsFetchingPanFn(false);
        setLoadingTextFn("");
        return;
      }

      if (!isNameMatch) {
        if (!isNameLongEnough) {
          formInstance.pushError({
            pan: [
              "Please enter your name (minimum 3 characters) before PAN verification.",
            ],
          });
        } else {
          formInstance.pushError({
            pan: [
              "The name doesn't match your PAN records. Please enter your name exactly as on your PAN card.",
            ],
          });
        }
        setIsNameVerifiedFn(false);
        setIsFetchingPanFn(false);
        setLoadingTextFn("");
        return;
      }

      // Success - clear errors
      formInstance.pushError({ pan: [] });

      // V4 mode: Don't overwrite user's entered name
      // Legacy mode: Use full_name from API response
      if (!isDigioPanFullVerificationEnabled) {
        formInstance.setField("name", res?.full_name);
      }
      setIsNameVerifiedFn(true);
      setIsFetchingPanFn(false);
      setLoadingTextFn("");
    } catch (error: any) {
      console.log("PAN verification error:", error);

      if (error?.response?.status === 422) {
        const errorMessage =
          error?.response?.data?.message?.message ||
          "Validation failed. Please check your details.";
        formInstance.pushError({
          pan: [errorMessage],
        });
      } else {
        formInstance.pushError({
          pan: ["An internal error occurred. Please try again later."],
        });
      }

      if (!isDigioPanFullVerificationEnabled) {
        formInstance.setField("name", "");
      }
      setIsNameVerifiedFn(false);
      setIsFetchingPanFn(false);
      setLoadingTextFn("");
    }
  };

  useEffect(() => {
    form.setValidationRules(form_constraints);
  }, [form.value]);
  const [bidderData, setBidderData] = useState<any>(null);
  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadingText("Loading Assigned representative Data.Please wait...");
      const data: any = await getBidderByUuid(bidderUuid);
      setBidderData(data);
      setStage(data?.application_form_data?.ar_step || null);

      // Load existing AR details if available
      const arDetails = data?.application_form_data?.ar_details;
      if (arDetails) {
        form.setField("mobile", arDetails?.user_mobile || "");
        form.setField("email", arDetails?.email || "");
        form.setField("pan", arDetails?.pan || "");
        form.setField("name", arDetails?.pan_name || "");
        form.setField("designation", arDetails?.designation || "");

        // Load existing authorization letter file if available
        if (arDetails?.ar_authorization_letter?.id) {
          try {
            const authLetterFile: any = await getFileByUuid(
              arDetails.ar_authorization_letter.id,
            );
            if (authLetterFile) {
              const formattedFile = {
                id: authLetterFile?.uuid,
                filename:
                  authLetterFile?.file_name ||
                  authLetterFile?.filename ||
                  "Authorization Letter",
                mime: authLetterFile?.mime || "",
              };
              setKycFiles([formattedFile]);
              form.setField("ar_authorization_letter", formattedFile.id || "");
              setRenderKey((prev) => prev + 1); // Force re-render of file uploader
            }
          } catch (error) {
            console.error(
              "Error loading existing authorization letter:",
              error,
            );
          }
        }
      }

      setLoading(false);
      setLoadingText("");
    })();
  }, [bidderUuid]);

  const handleSubmit = async () => {
    setLoading(true);
    console.log("form", form);
    if (form.validate()) {
      const data: any = await getBidderByUuid(bidderUuid);
      const res5: any = await updateBidderForEvent({
        uuid: bidderUuid,
        application_form_data: {
          ...data?.application_form_data,
          ar_details: {
            user_mobile: form.getField("mobile"),
            email: form.getField("email"),
            pan: form.getField("pan"),
            pan_name: form.getField("name"),
            designation: form.getField("designation"),
            ar_authorization_letter: kycFiles[0],
            // Clear rejection-related fields on resubmission
            status: "completed",
            reject_remarks: null,
            verification_step: null,
          },
          ar_step: "upload_ar_kyc_doc",
        },
      });
      setLoadingText(
        "Assigned representative. Redirecting to the next step. Please wait...",
      );
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await init();
      setStage("upload_ar_kyc_doc");
      setLoading(false);
    }
    setLoading(false);
  };
  return (
    <div>
      {stage !== "upload_ar_kyc_doc" ? (
        <div>
          <h4 className="font-bold text-lg font-openSans">
            {stepsTitles?.[activeStep]?.label}
          </h4>
          <div className="mt-4 min-h-[300px]">
            {/* Rejection Status UI */}
            {(bidderData?.application_form_data?.ar_details?.status ===
              "rejected" ||
              (bidderData?.application_form_data?.verification_step ===
                "add_ar" &&
                bidderData?.application_form_data?.status === "rejected")) && (
              <div className="mb-6">
                <div className="border-[1px] border-red-300 bg-red-50 px-4 py-3 rounded-md">
                  <div className="flex gap-2 md:gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <ExclamationIcon className="text-red-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm md:text-base font-semibold text-red-700 mb-2">
                        Representative Details Rejected
                      </div>
                      <div className="text-xs md:text-sm text-red-600 mb-2">
                        <span className="font-semibold">
                          Rejection Remarks:
                        </span>{" "}
                        {bidderData?.application_form_data?.ar_details
                          ?.reject_remarks ||
                          bidderData?.application_form_data?.reject_remarks ||
                          "No rejection remarks provided"}
                      </div>
                      <div className="text-xs md:text-sm text-red-700 leading-relaxed">
                        Please review the remarks above and update the
                        representative details accordingly.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="text-xs md:text-sm font-openSans text-brand-textGrey">
              Please enter the details of the authorized representative below:
            </div>
            <div className="mt-4">
              {/* V4 mode: Email field comes FIRST - before name and PAN */}
              {isDigioPanFullVerificationEnabled && (
                <div className="mt-4">
                  <Input
                    placeholder="Email*"
                    value={form.getField("email")}
                    onChange={(e: any) => {
                      const input = e.target;
                      const start = input?.selectionStart;
                      const end = input?.selectionEnd;
                      const lower = input.value?.toLowerCase();
                      form.setField("email", lower);
                      requestAnimationFrame(() => {
                        input.setSelectionRange(start, end);
                      });
                    }}
                    className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
                  />
                  {form.errors.get("email") ? (
                    <p className="mt-1 text-sm text-red-500">
                      {form.errors.get("email")?.[0]}
                    </p>
                  ) : null}
                </div>
              )}

              {/* V4 mode: Name field - AFTER email, BEFORE PAN */}
              {isDigioPanFullVerificationEnabled && (
                <div className="mt-4">
                  <div className="relative">
                    {isFetchingPan && (
                      <div className="absolute top-1/3 right-2 z-10">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-orange"></div>
                      </div>
                    )}
                    {isNameVerified && !isFetchingPan && (
                      <div className="absolute top-1/3 right-2 z-10">
                        <svg
                          className="h-5 w-5 text-green-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    )}
                    <Input
                      disabled={loading}
                      placeholder={"Name (as per PAN)*"}
                      value={form.getField("name")}
                      onChange={(e: any) => {
                        const value = e?.target?.value?.toUpperCase() || "";
                        form.setField("name", value);
                        form.pushError({ name: [] }); // Clear name errors when typing
                        // V4 name-first flow: trigger PAN verification after name >= 3 chars
                        triggerPanVerification();
                      }}
                      className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
                    />
                  </div>
                  {form.errors.get("name") ? (
                    <p className="mt-1 text-sm text-red-500">
                      {form.errors.get("name")?.[0]}
                    </p>
                  ) : null}
                </div>
              )}

              {/* PAN field - FIRST in legacy mode, AFTER name in V4 mode */}
              <div className="mt-4">
                <div className="relative">
                  {isFetchingPan && (
                    <div className="absolute top-1/3 right-2 z-10">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-orange"></div>
                    </div>
                  )}
                  <Input
                    disabled={
                      loading ||
                      (isDigioPanFullVerificationEnabled &&
                        (form.getField("name") || "").length < 3)
                    }
                    placeholder={"Representative PAN Number*"}
                    value={form.getField("pan")}
                    onChange={(e: any) => {
                      if (e?.target?.value?.length < 11) {
                        let value = e?.target?.value.toUpperCase();
                        form.setField("pan", value);
                        form.pushError({ pan: [] }); // Clear errors when PAN changes

                        // V4 mode: trigger verification when PAN is complete AND name >= 3 chars
                        if (isDigioPanFullVerificationEnabled) {
                          const currentName = (form.getField("name") || "")
                            .toString()
                            .trim();
                          if (
                            currentName.length >= 3 &&
                            value.length === 10 &&
                            panRegex.test(value)
                          ) {
                            triggerPanVerification();
                          } else if (value.length > 0 && value.length < 10) {
                            // Clear verification state if PAN is incomplete
                            setIsNameVerified(false);
                          }
                        }

                        // Legacy mode: verify immediately on valid PAN
                        if (
                          !isDigioPanFullVerificationEnabled &&
                          value.length === 10 &&
                          panRegex.test(value)
                        ) {
                          handlePanVerification(
                            value,
                            form,
                            setIsNameVerified,
                            setIsFetchingPan,
                            setLoadingText,
                          );
                        } else if (!isDigioPanFullVerificationEnabled) {
                          // Legacy mode: clear name when PAN changes
                          form.setField("name", "");
                          setIsNameVerified(false);
                        }
                      }
                    }}
                    className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
                  />
                </div>
                {/* Show name verification status in V4 mode */}
                {isDigioPanFullVerificationEnabled &&
                  isNameVerified &&
                  !isFetchingPan && (
                    <div className="text-green-600 capitalize text-xs ml-3 mt-1 flex items-center gap-1">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {form.getField("name")} - PAN Verified
                    </div>
                  )}
                {form.errors.get("pan") ? (
                  <p className="mt-1 text-sm text-red-500">
                    {form.errors.get("pan")?.[0]}
                  </p>
                ) : null}
              </div>

              {/* Mobile field */}
              <div className="mt-4">
                <Input
                  type="number"
                  placeholder={"Mobile*"}
                  value={form.getField("mobile")}
                  onChange={(e: any) => {
                    if (e.target.value >= 0 || e.target.value == "") {
                      form.setField("mobile", e?.target?.value);
                      form.pushError({
                        mobile: [],
                      });
                    }
                  }}
                  className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
                />
                {form.errors.get("mobile") ? (
                  <p className="mt-1 text-sm text-red-500">
                    {form.errors.get("mobile")?.[0]}
                  </p>
                ) : null}
              </div>

              {/* Legacy mode: Email field comes AFTER mobile - PAN autofills name from API */}
              {!isDigioPanFullVerificationEnabled && (
                <div className="mt-4">
                  <Input
                    placeholder="Email*"
                    value={form.getField("email")}
                    onChange={(e: any) => {
                      const input = e.target;
                      const start = input?.selectionStart;
                      const end = input?.selectionEnd;
                      const lower = input.value?.toLowerCase();
                      form.setField("email", lower);
                      requestAnimationFrame(() => {
                        input.setSelectionRange(start, end);
                      });
                    }}
                    className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
                  />
                  {form.errors.get("email") ? (
                    <p className="mt-1 text-sm text-red-500">
                      {form.errors.get("email")?.[0]}
                    </p>
                  ) : null}
                </div>
              )}

              {/* Designation field */}
              <div className="mt-4">
                <Input
                  disabled={loading}
                  placeholder={"Designation*"}
                  value={form.getField("designation")}
                  onChange={async (e: any) => {
                    form.setField("designation", e?.target?.value);
                  }}
                  className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
                />
                {form.errors.get("designation") ? (
                  <p className="mt-1 text-sm text-red-500">
                    {form.errors.get("designation")[0]}
                  </p>
                ) : null}
              </div>
              <div className="mt-4">
                <CustomMultipleFileUploader
                  key={renderKey}
                  handleChange={(data: any) => {
                    console.log("id", data?.[0]?.id);
                    form.setField(
                      "ar_authorization_letter",
                      data?.[0]?.id || "",
                    );
                    // console.log("test3e4567890-", data);
                    setKycFiles(data);
                  }}
                  allowedMimeTypes={[
                    "image/png",
                    "image/jpeg",
                    "application/pdf",
                  ]}
                  property_uuid={bidderUuid}
                  owner_service_type="ar-authorization-letter"
                  data={kycFiles}
                  maxAllowed={1}
                  defaultText="Please upload Authorization Letter / Board Resolution"
                />
                {form.errors.get("ar_authorization_letter") ? (
                  <p className="mt-1 text-sm text-red-500">
                    {form.errors.get("ar_authorization_letter")[0]}
                  </p>
                ) : null}
              </div>
              <div className="mt-8 flex justify-center">
                <Button
                  disabled={loading}
                  className="w-full max-w-xl h-10 text-[18px]"
                  onClick={handleSubmit}
                >
                  {/* Next */}
                  Submit
                </Button>
              </div>
            </div>
          </div>
          {(loading || isFetchingPan) && (
            <LoadingModal loadingText={loadingText} />
          )}
        </div>
      ) : (
        <div>
          <h4 className="font-bold text-lg font-openSans">
            {stepsTitles?.[activeStep]?.label} Documents
          </h4>
          <AuthorizedRepresentativeDocumentUploader
            loading={loading}
            setLoading={setLoading}
            bidderUuid={bidderUuid}
            bidder={bidderData}
            arName={
              form.getField("name") ||
              bidderData?.application_form_data?.ar_details?.pan_name ||
              ""
            }
            arPan={bidderData?.application_form_data?.ar_details?.pan}
            setLoadingText={setLoadingText}
            init={init}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
          />
          {loading && <LoadingModal loadingText={loadingText} />}
        </div>
      )}
    </div>
  );
}
interface LoadingModalProps {
  loadingText?: string;
}
const LoadingModal = ({ loadingText }: LoadingModalProps) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className=" text-white flex flex-col relative">
        <div className="flex flex-col flex-grow h-full">
          <div className="flex justify-center items-center">
            <div className="w-12 h-12 border-8 border-t-6 border-brand-orange rounded-full animate-spin bg-gradient-to-r from-brand-orange to-blue-100 shadow-lg"></div>
          </div>
          <div className="mt-2 text-sm font-openSans text-center">
            {loadingText || "Validating Details. Please wait..."}
          </div>
        </div>
      </div>
    </div>
  );
};
interface AuthorizedRepresentativeDocumentUploaderProps {
  bidderUuid: string;
  bidder?: any;
  loading?: boolean;
  setLoading: any;
  arName: string;
  arPan: any;
  setLoadingText: any;
  init: any;
  setActiveStep: any;
  activeStep: any;
}
const AuthorizedRepresentativeDocumentUploader = ({
  bidderUuid,
  bidder,
  loading,
  setLoading,
  arName,
  arPan,
  setLoadingText,
  init,
  activeStep,
  setActiveStep,
}: AuthorizedRepresentativeDocumentUploaderProps) => {
  const [renderKey, setRenderKey] = useState(0);
  const [arPanFiles, setArPanFiles] = useState<any[]>([]);
  const [arAadhaarFrontFiles, setArAadhaarFrontFiles] = useState<any[]>([]);
  const [arPanOCRData, setArPanOCRData] = useState<any>({});
  const [arAadhaarBackFiles, setArAadhaarBackFiles] = useState<any[]>([]);
  const [arAddharOCRData, setArAadharOCRData] = useState<any>({});

  // Name mismatch handling states
  const [panNameMismatchWarning, setPanNameMismatchWarning] =
    useState<string>("");
  const [aadhaarNameMismatchWarning, setAadhaarNameMismatchWarning] =
    useState<string>("");
  const [showNameMismatchModal, setShowNameMismatchModal] = useState(false);
  const [nameMismatchModalType, setNameMismatchModalType] = useState<
    "pan" | "aadhaar" | "both"
  >("pan");
  const [nameMismatchDetails, setNameMismatchDetails] = useState<{
    panDocumentName: string;
    aadhaarDocumentName: string;
    profileName: string;
  }>({ panDocumentName: "", aadhaarDocumentName: "", profileName: "" });
  const [panNameMismatchConfirmed, setPanNameMismatchConfirmed] =
    useState(false);
  const [aadhaarNameMismatchConfirmed, setAadhaarNameMismatchConfirmed] =
    useState(false);
  const [supportingDocuments, setSupportingDocuments] = useState<any[]>([]);
  const [nameMismatchReason, setNameMismatchReason] = useState<string>("");
  const [isOcrLoading, setIsOcrLoading] = useState<boolean>(false);

  const supportingDocsUploaderRef = React.useRef<any>(null);
  const arAadhaarFrontUploaderRef = React.useRef<any>(null);
  const arAadhaarBackUploaderRef = React.useRef<any>(null);
  const arPanUploaderRef = React.useRef<any>(null);

  // Get mode from bidder data
  const isOnlineMode = bidder?.meta?.bid_application_flow_mode === "online";

  const form = useBetaForm({
    is_pan_verified: "",
    is_aadhar_front_verified: "",
    is_aadhar_back_verified: "",
    is_both_side_aadhar_uploaded: false,
  });
  const form_constraints = {
    is_pan_verified: {
      presence: {
        allowEmpty: false,
        message: "^Pan must be verified before submitting.",
      },
      inclusion: {
        within: [true],
        message: "^Pan must be verified before submitting.",
      },
    },
    is_aadhar_front_verified: {
      presence: {
        allowEmpty: false,
        message: "^Aadhar Front must be verified before submitting.",
      },
      inclusion: {
        within: [true],
        message: "^Aadhar Front be verified before submitting.",
      },
    },
    is_aadhar_back_verified: {
      presence: {
        allowEmpty: false,
        message: "^Aadhar Back must be verified before submitting.",
      },
      inclusion: {
        within: [true],
        message: "^Aadhar Back must be verified before submitting.",
      },
    },
  };
  useEffect(() => {
    form.setValidationRules(form_constraints);
  }, [form.value]);

  // Load existing AR KYC documents from ar_doc_details if available
  useEffect(() => {
    const loadExistingArDocs = async () => {
      const arDocDetails = bidder?.application_form_data?.ar_doc_details;
      if (!arDocDetails) return;

      try {
        // Load PAN document
        if (arDocDetails.pan) {
          const panFile: any = await getFileByUuid(arDocDetails.pan);
          if (panFile) {
            const formattedPanFile = {
              id: panFile?.uuid,
              filename:
                panFile?.file_name || panFile?.filename || "AR PAN Document",
              mime: panFile?.mime || "",
            };
            setArPanFiles([formattedPanFile]);
            form.setField("is_pan_verified", true);
            if (arDocDetails.pan_ocr_data) {
              setArPanOCRData({
                detections: [{ id_attributes: arDocDetails.pan_ocr_data }],
              });
            }
          }
        }

        // Load Aadhaar front document
        if (arDocDetails.aadhaar_front) {
          const aadharFrontFile: any = await getFileByUuid(
            arDocDetails.aadhaar_front,
          );
          if (aadharFrontFile) {
            const formattedFrontFile = {
              id: aadharFrontFile?.uuid,
              filename:
                aadharFrontFile?.file_name ||
                aadharFrontFile?.filename ||
                "AR Aadhaar Front",
              mime: aadharFrontFile?.mime || "",
            };
            setArAadhaarFrontFiles([formattedFrontFile]);
          }
        }

        // Load Aadhaar back document
        if (arDocDetails.aadhaar_back) {
          const aadharBackFile: any = await getFileByUuid(
            arDocDetails.aadhaar_back,
          );
          if (aadharBackFile) {
            const formattedBackFile = {
              id: aadharBackFile?.uuid,
              filename:
                aadharBackFile?.file_name ||
                aadharBackFile?.filename ||
                "AR Aadhaar Back",
              mime: aadharBackFile?.mime || "",
            };
            setArAadhaarBackFiles([formattedBackFile]);
          }
        }

        // Load Aadhaar OCR data if present
        if (arDocDetails.aadhaar_ocr_data) {
          setArAadharOCRData({
            detections: [{ id_attributes: arDocDetails.aadhaar_ocr_data }],
          });
        }

        // Load name mismatch supporting documents, if any
        if (
          Array.isArray(arDocDetails.name_mismatch_supporting_documents) &&
          arDocDetails.name_mismatch_supporting_documents.length > 0
        ) {
          const supportingPromises =
            arDocDetails.name_mismatch_supporting_documents.map(
              (uuid: string) => getFileByUuid(uuid),
            );
          const supportingFilesData: any = await Promise.all(
            supportingPromises,
          );
          const formattedSupportingFiles = supportingFilesData
            .filter(Boolean)
            .map((item: any) => ({
              id: item?.uuid,
              filename:
                item?.file_name ||
                item?.filename ||
                "Name Mismatch Supporting Document",
              mime: item?.mime || "",
            }));
          if (formattedSupportingFiles.length > 0) {
            setSupportingDocuments(formattedSupportingFiles);
          }
        }

        // Load name mismatch reason and confirmations
        if (arDocDetails.name_mismatch_reason) {
          setNameMismatchReason(arDocDetails.name_mismatch_reason);
        }
        if (arDocDetails.pan_name_mismatch_confirmed) {
          setPanNameMismatchConfirmed(true);
        }
        if (arDocDetails.aadhaar_name_mismatch_confirmed) {
          setAadhaarNameMismatchConfirmed(true);
        }

        // If Aadhaar docs exist, consider both sides uploaded
        if (arDocDetails.aadhaar_front && arDocDetails.aadhaar_back) {
          form.setField("is_aadhar_front_verified", true);
          form.setField("is_aadhar_back_verified", true);
          form.setField("is_both_side_aadhar_uploaded", true);
        }

        // Force re-render for any uploaders depending on local state
        setRenderKey((prev) => prev + 1);
      } catch (error) {
        console.error("Error loading existing AR KYC documents:", error);
      }
    };

    loadExistingArDocs();
  }, [bidder, form]);

  const handlePanDocumentChange = async (data: any) => {
    console.log("id", data?.[0]?.id);

    if (data?.[0]?.id) {
      setLoading(true);
      setLoadingText(
        "Verifying Authorized Representative's PAN. Please wait...",
      );
      form.removeError("is_pan_verified");
      form.setField("is_pan_verified", false);
      setPanNameMismatchWarning("");

      let res: any = null;
      let attempt = 0;
      let success = false;

      setIsOcrLoading(true);
      while (attempt < 3 && !success) {
        try {
          res = await fetchIDCardOCRDetails({
            enable_both_sides: false,
            check_id_match: false,
            front_part: data?.[0]?.id,
          });
          console.log(`Attempt ${attempt + 1}:`, res);
          if (res?.detections?.length) {
            success = true;
            form.removeError("is_pan_verified");
          }
        } catch (error: any) {
          console.log(
            `Error on attempt ${attempt + 1}:`,
            error,
            error?.response?.data?.status,
          );
          if (error?.response?.status === 400) {
            const message: any = error?.response?.data?.errorMessage;
            form.setErrors({ is_pan_verified: [message] });
            attempt = 4;
          }
          if (error?.response?.status >= 500) {
            const message: any = error?.response?.data?.errorMessage;
            form.setErrors({ is_pan_verified: [message] });
            attempt = 4;
          }
          attempt++;
          if (attempt < 3) {
            await new Promise((resolve) => setTimeout(resolve, 18000));
            if (attempt === 1) {
              setLoadingText(
                "This is taking longer than expected. Please wait a moment...",
              );
            }
          } else if (attempt === 3) {
            const message: any = error?.response?.data?.errorMessage;
            form.setErrors({ is_pan_verified: [message] });
          }
        }
      }

      setIsOcrLoading(false);
      if (!success) {
        await arPanUploaderRef.current?.deleteFile(data?.[0]?.id);
        form.setField("is_pan_verified", false);
        setLoading(false);
        setLoadingText("");
        return;
      }

      if (res?.detections?.length > 0) {
        const detection = res.detections[0];

        if (detection.id_type !== "PAN") {
          setIsOcrLoading(false);
          form.setErrors({
            is_pan_verified: [
              "Please upload a valid PAN document of the Authorized Representative.",
            ],
          });
          setLoading(false);
          await arPanUploaderRef.current?.deleteFile(data?.[0]?.id);
          return;
        }

        if (detection?.detected_part !== "FRONT") {
          setIsOcrLoading(false);
          form.setErrors({
            is_pan_verified: [
              "Upload a valid FRONT side of your PAN document to continue.",
            ],
          });
          setLoading(false);
          await arPanUploaderRef.current?.deleteFile(data?.[0]?.id);
          return;
        }
        if (
          detection?.id_attributes?.id_no?.toLowerCase() !==
          arPan?.toLowerCase()
        ) {
          setIsOcrLoading(false);
          form.setErrors({
            is_pan_verified: [
              `The uploaded PAN (${
                detection?.id_attributes?.id_no ?? ""
              }) does not match with AR PAN (${
                arPan ?? ""
              }). Please check and try.`,
            ],
          });
          setLoading(false);
          await arPanUploaderRef.current?.deleteFile(data?.[0]?.id);
          return;
        }

        // Step 3: Perform fuzzy name matching between document name and AR name
        const fuzzyMatchPayload: any = {
          context: "name",
          target_text: arName,
          source_text: detection?.id_attributes?.name || "",
        };

        const res2: any = await fetchFuzzyMatchDetails(fuzzyMatchPayload);

        // If name doesn't match (even with fuzzy matching)
        if (!res2?.matched && res2?.match_score < 50) {
          if (isOnlineMode) {
            setIsOcrLoading(false);
            form.setErrors({
              is_pan_verified: [
                `Name mismatch below threshold. Document name: ${fuzzyMatchPayload[
                  "source_text"
                ]?.toLowerCase()}, AR name is: ${arName?.toLowerCase()}. Please contact support.`,
              ],
            });
            setLoading(false);
            await arPanUploaderRef.current?.deleteFile(data?.[0]?.id);
            return;
          } else {
            setIsOcrLoading(false);
            form.setErrors({
              is_pan_verified: [
                `Name mismatch below threshold. The name on the document (${
                  fuzzyMatchPayload["source_text"]?.toUpperCase() ?? ""
                }) does not match the name in your profile (${
                  arName?.toUpperCase() ?? ""
                }). The matching score is below the required threshold. Please contact support for assistance or try again.`,
              ],
            });
            setLoading(false);
            await arPanUploaderRef.current?.deleteFile(data?.[0]?.id);
            return;
          }
        } else if (!res2?.matched) {
          // For online mode, reject name mismatch
          if (isOnlineMode) {
            setIsOcrLoading(false);
            form.setErrors({
              is_pan_verified: [
                `Name validation failed. Document name: ${fuzzyMatchPayload[
                  "source_text"
                ]?.toLowerCase()}, AR name is: ${arName?.toLowerCase()}. Please contact support.`,
              ],
            });
            setLoading(false);
            await arPanUploaderRef.current?.deleteFile(data?.[0]?.id);
            return;
          } else {
            // For offline mode, allow name mismatch but require confirmation
            setArPanOCRData(res);
            setPanNameMismatchWarning(
              `Name mismatch detected. Document name: ${fuzzyMatchPayload[
                "source_text"
              ]?.toUpperCase()}, AR name is: ${arName?.toUpperCase()}. Please ensure the document belongs to the Authorized Representative.`,
            );
            form.setField("is_pan_verified", false);
            form.removeError("is_pan_verified");
          }
        } else {
          // Name matches - mark as verified
          setPanNameMismatchWarning("");
          setPanNameMismatchConfirmed(false);
          form.setField("is_pan_verified", true);
          setArPanOCRData(res);
        }
      }

      form.setField("pan_uuid", data?.[0]?.id || "");
      setArPanFiles(data);
      setIsOcrLoading(false);
      setLoadingText("");
      setLoading(false);
    }
  };

  const handlePanDocumentRemove = async () => {
    if (arPanFiles?.[0]?.id) {
      setLoading(true);
      setLoadingText("Removing PAN document. Please wait...");

      await arPanUploaderRef.current?.deleteFile(arPanFiles[0].id);
      setArPanFiles([]);
      setArPanOCRData({});
      setPanNameMismatchWarning("");
      setPanNameMismatchConfirmed(false);
      form.setField("is_pan_verified", false);
      form.setField("pan_uuid", "");
      form.removeError("is_pan_verified");

      setLoading(false);
      setLoadingText("");
    }
  };
  const handleAadhaarDocumentVerification = async (
    arAadhaarFrontFiles: any,
    arAadhaarBackFiles: any,
  ) => {
    if (arAadhaarFrontFiles.length > 0 && arAadhaarBackFiles.length > 0) {
      setLoadingText("");
      setLoading(true);
      setLoadingText(
        "Verifying Aadhaar of Authorized Representative. Please wait...",
      );
      form.removeError("is_aadhar_front_verified");
      form.removeError("is_aadhar_back_verified");
      form.setField("is_both_side_aadhar_uploaded", false);
      form.setField("is_aadhar_front_verified", false);
      form.setField("is_aadhar_back_verified", false);
      setAadhaarNameMismatchWarning("");

      setIsOcrLoading(true);
      let res: any = null;
      let attempt = 0;
      let success = false;
      while (attempt < 3 && !success) {
        try {
          res = await fetchIDCardOCRDetails({
            enable_both_sides: true,
            check_id_match: false,
            front_part: arAadhaarFrontFiles?.[0]?.id,
            back_part: arAadhaarBackFiles?.[0]?.id,
          });
          console.log(`Attempt ${attempt + 1}:`, res);
          if (res?.detections?.length) {
            success = true;
            form.removeError("is_aadhar_back_verified");
          }
        } catch (error: any) {
          console.log(`Error on attempt ${attempt + 1}:`, error);
          if (error?.response?.status === 400) {
            const message: any = error?.response?.data?.errorMessage;
            form.setErrors({ is_aadhar_back_verified: [message] });
            attempt = 4;
          }
          if (error?.response?.status >= 500) {
            const message: any = error?.response?.data?.errorMessage;
            form.setErrors({ is_aadhar_back_verified: [message] });
            attempt = 4;
          }
          attempt++;
          if (attempt < 3) {
            await new Promise((resolve) => setTimeout(resolve, 18000));
            setLoadingText(
              "This is taking longer than expected. Please wait a moment...",
            );
          } else if (attempt === 3) {
            const message: any = error?.response?.data?.errorMessage;
            console.log("message: ", message, error?.response, error);
            form.setErrors({ is_aadhar_back_verified: [message] });
          }
        }
      }

      setIsOcrLoading(false);
      if (!success) {
        await arAadhaarFrontUploaderRef.current?.deleteFile(
          arAadhaarFrontFiles?.[0]?.id,
        );
        await arAadhaarBackUploaderRef.current?.deleteFile(
          arAadhaarBackFiles?.[0]?.id,
        );
        form.setField("is_both_side_aadhar_uploaded", false);
        setLoadingText("");
        setLoading(false);
        return;
      } else {
        if (res?.detections?.length > 0) {
          const detection = res.detections[0];
          if (detection.id_type !== "AADHAAR") {
            setIsOcrLoading(false);
            form.setErrors({
              is_aadhar_back_verified: [
                "Please upload a valid Aadhaar document.",
              ],
            });
            setLoading(false);
            await arAadhaarFrontUploaderRef.current?.deleteFile(
              arAadhaarFrontFiles?.[0]?.id,
            );
            await arAadhaarBackUploaderRef.current?.deleteFile(
              arAadhaarBackFiles?.[0]?.id,
            );
            return;
          }
          if (detection?.detected_part !== "BOTH") {
            setIsOcrLoading(false);
            form.setErrors({
              is_aadhar_back_verified: [
                "Please upload both FRONT and BACK sides of your Aadhaar document to continue.",
              ],
            });
            setLoading(false);
            await arAadhaarFrontUploaderRef.current?.deleteFile(
              arAadhaarFrontFiles?.[0]?.id,
            );
            await arAadhaarBackUploaderRef.current?.deleteFile(
              arAadhaarBackFiles?.[0]?.id,
            );
            return;
          }
          const fuzzyMatchPayload: any = {
            context: "name",
            target_text: arName,
            source_text: detection?.id_attributes?.name || "",
          };
          const res2: any = await fetchFuzzyMatchDetails(fuzzyMatchPayload);
          if (!res2?.matched && res2?.match_score < 50) {
            setIsOcrLoading(false);
            form.setErrors({
              is_aadhar_back_verified: [
                `Name mismatch below threshold. Document name: ${fuzzyMatchPayload[
                  "source_text"
                ]?.toUpperCase()}, Profile Name is: ${arName?.toUpperCase()}. The matching score is below the required threshold. Please contact support for assistance or try again.`,
              ],
            });
            setLoading(false);
            await arAadhaarFrontUploaderRef.current?.deleteFile(
              arAadhaarFrontFiles?.[0]?.id,
            );
            await arAadhaarBackUploaderRef.current?.deleteFile(
              arAadhaarBackFiles?.[0]?.id,
            );
            return;
          } else if (!res2?.matched) {
            // For online mode, reject name mismatch
            if (isOnlineMode) {
              setIsOcrLoading(false);
              form.setErrors({
                is_aadhar_back_verified: [
                  `Name validation failed. Document name: ${fuzzyMatchPayload[
                    "source_text"
                  ]?.toLowerCase()}, AR name is: ${arName?.toLowerCase()}. Please contact support.`,
                ],
              });
              setLoading(false);
              await arAadhaarFrontUploaderRef.current?.deleteFile(
                arAadhaarFrontFiles?.[0]?.id,
              );
              await arAadhaarBackUploaderRef.current?.deleteFile(
                arAadhaarBackFiles?.[0]?.id,
              );
              return;
            } else {
              // For offline mode, allow name mismatch but require confirmation
              setArAadharOCRData(res);
              setAadhaarNameMismatchWarning(
                `Name mismatch detected. Document name: ${fuzzyMatchPayload[
                  "source_text"
                ]?.toUpperCase()}, AR name is: ${arName?.toUpperCase()}. Please ensure the document belongs to the Authorized Representative.`,
              );
              form.setField("is_aadhar_front_verified", false);
              form.setField("is_aadhar_back_verified", false);
              form.setField("is_both_side_aadhar_uploaded", false);
              form.removeError("is_aadhar_front_verified");
              form.removeError("is_aadhar_back_verified");
            }
          } else {
            // Name matches - mark as verified
            setAadhaarNameMismatchWarning("");
            setAadhaarNameMismatchConfirmed(false);
            form.setField("is_aadhar_front_verified", true);
            form.setField("is_aadhar_back_verified", true);
            form.setField("is_both_side_aadhar_uploaded", true);
            setArAadharOCRData(res);
          }
        }
      }
      setIsOcrLoading(false);
      setLoadingText("");
      setLoading(false);
    }
  };

  const handleAadhaarFrontRemove = async () => {
    if (arAadhaarFrontFiles?.[0]?.id) {
      setLoading(true);
      setLoadingText("Removing Aadhaar front document. Please wait...");

      await arAadhaarFrontUploaderRef.current?.deleteFile(
        arAadhaarFrontFiles[0].id,
      );

      setArAadhaarFrontFiles([]);
      form.setField("is_aadhar_front_verified", false);
      form.setField("is_both_side_aadhar_uploaded", false);
      form.removeError("is_aadhar_front_verified");

      if (form.getField("is_aadhar_back_verified") === true) {
        form.setField("is_aadhar_back_verified", false);
        form.removeError("is_aadhar_back_verified");
        setArAadharOCRData({});
        setAadhaarNameMismatchWarning("");
        setAadhaarNameMismatchConfirmed(false);
      }

      setLoading(false);
      setLoadingText("");
    }
  };

  const handleAadhaarBackRemove = async () => {
    if (arAadhaarBackFiles?.[0]?.id) {
      setLoading(true);
      setLoadingText("Removing Aadhaar back document. Please wait...");

      await arAadhaarBackUploaderRef.current?.deleteFile(
        arAadhaarBackFiles[0].id,
      );

      setArAadhaarBackFiles([]);
      form.setField("is_aadhar_back_verified", false);
      form.setField("is_both_side_aadhar_uploaded", false);
      form.removeError("is_aadhar_back_verified");

      if (form.getField("is_aadhar_front_verified") === true) {
        form.setField("is_aadhar_front_verified", false);
        form.removeError("is_aadhar_front_verified");
        setArAadharOCRData({});
        setAadhaarNameMismatchWarning("");
        setAadhaarNameMismatchConfirmed(false);
      }

      setLoading(false);
      setLoadingText("");
    }
  };

  const handleNameMismatchConfirm = () => {
    // Check if all required confirmations and documents are ready
    const needsPanConfirm =
      nameMismatchModalType === "pan" || nameMismatchModalType === "both";
    const needsAadhaarConfirm =
      nameMismatchModalType === "aadhaar" || nameMismatchModalType === "both";

    const panReady =
      !needsPanConfirm ||
      (panNameMismatchConfirmed && supportingDocuments.length > 0);
    const aadhaarReady =
      !needsAadhaarConfirm ||
      (aadhaarNameMismatchConfirmed && supportingDocuments.length > 0);

    // Check if reason is provided
    if (!nameMismatchReason || nameMismatchReason.trim() === "") {
      return;
    }

    if (panReady && aadhaarReady) {
      // Mark documents as verified
      if (needsPanConfirm) {
        form.setField("is_pan_verified", true);
      }
      if (needsAadhaarConfirm) {
        form.setField("is_aadhar_front_verified", true);
        form.setField("is_aadhar_back_verified", true);
        form.setField("is_both_side_aadhar_uploaded", true);
      }
      // Close modal and clear warnings
      setShowNameMismatchModal(false);
      setPanNameMismatchWarning("");
      setAadhaarNameMismatchWarning("");
    }
  };
  const handleSubmit = async () => {
    setLoading(true);

    const hasPanMismatch = panNameMismatchWarning.length > 0;
    const hasAadhaarMismatch = aadhaarNameMismatchWarning.length > 0;

    const panNeedsConfirm =
      hasPanMismatch &&
      (!panNameMismatchConfirmed ||
        supportingDocuments.length === 0 ||
        !nameMismatchReason ||
        nameMismatchReason.trim() === "");
    const aadhaarNeedsConfirm =
      hasAadhaarMismatch &&
      (!aadhaarNameMismatchConfirmed ||
        supportingDocuments.length === 0 ||
        !nameMismatchReason ||
        nameMismatchReason.trim() === "");

    if (panNeedsConfirm || aadhaarNeedsConfirm) {
      setLoading(false);
      if (!showNameMismatchModal) {
        const modalType =
          panNeedsConfirm && aadhaarNeedsConfirm
            ? "both"
            : panNeedsConfirm
            ? "pan"
            : "aadhaar";
        setNameMismatchModalType(modalType);
        setNameMismatchDetails({
          panDocumentName:
            arPanOCRData?.detections?.[0]?.id_attributes?.name || "",
          aadhaarDocumentName:
            arAddharOCRData?.detections?.[0]?.id_attributes?.name || "",
          profileName: arName || "",
        });
        setShowNameMismatchModal(true);
      }
      return;
    }

    if (
      !hasPanMismatch ||
      (panNameMismatchConfirmed && supportingDocuments.length > 0)
    ) {
      form.setField("is_pan_verified", true);
    }
    if (
      !hasAadhaarMismatch ||
      (aadhaarNameMismatchConfirmed && supportingDocuments.length > 0)
    ) {
      form.setField("is_aadhar_front_verified", true);
      form.setField("is_aadhar_back_verified", true);
      form.setField("is_both_side_aadhar_uploaded", true);
    }

    if (form.validate()) {
      setLoadingText(
        "Saving the Authorized Representative details. Please wait...",
      );
      const data: any = await getBidderByUuid(bidderUuid);

      const supportingDocs = supportingDocuments.map(
        (doc: any) => doc.id || doc,
      );
      let aadhaarFrontData: any = null;
      if (arAadhaarFrontFiles?.[0]?.id) {
        try {
          aadhaarFrontData = await maskUploadedAadhaar({
            aadhaar_uuid: arAadhaarFrontFiles?.[0]?.id,
            aadhaar_side: "front",
          });
        } catch (error) {
          console.log("error: ", error);
        }
      }
      let aadhaarBackData: any = null;
      if (arAadhaarBackFiles?.[0]?.id) {
        try {
          aadhaarBackData = await maskUploadedAadhaar({
            aadhaar_uuid: arAadhaarBackFiles?.[0]?.id,
            aadhaar_side: "back",
          });
        } catch (error) {
          console.log("error: ", error);
        }
      }

      const res5: any = await updateBidderForEvent({
        uuid: bidderUuid,
        meta: {
          ...data?.meta,
          steps_status: {
            ...data?.meta?.steps_status,
            add_ar: "completed",
          },
        },
        application_form_data: {
          ...data?.application_form_data,
          ar_doc_details: {
            status: "completed",
            pan: arPanFiles?.[0]?.id || "",
            aadhaar_front:
              aadhaarFrontData?.data?.uuid ||
              arAadhaarFrontFiles?.[0]?.id ||
              "",
            aadhaar_back:
              aadhaarBackData?.data?.uuid || arAadhaarBackFiles?.[0]?.id || "",
            pan_name_mismatch_confirmed: panNameMismatchConfirmed || false,
            aadhaar_name_mismatch_confirmed:
              aadhaarNameMismatchConfirmed || false,
            name_mismatch_supporting_documents: supportingDocs,
            name_mismatch_reason: nameMismatchReason || "",
            is_name_mismatch_supporting_documents_uploaded:
              supportingDocs.length > 0,
            pan_ocr_data: arPanOCRData?.detections?.[0]?.id_attributes,
            aadhaar_ocr_data: arAddharOCRData?.detections?.[0]?.id_attributes,
          },
          ar_step: "ar_kyc_doc_uploaded",
        },
        status: "pending",
        verification_step: "add_ar",
      });

      await new Promise((resolve) => setTimeout(resolve, 1000));
      setActiveStep(activeStep + 1);
      await init();
      setLoadingText("Redirecting to the next step. Please wait...");
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  return (
    <div className="font-openSans">
      {/* OCR Loading Modal */}
      {isOcrLoading && !loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="text-white flex flex-col relative">
            <div className="flex flex-col flex-grow h-full">
              <div className="flex justify-center items-center">
                <div className="w-12 h-12 border-8 border-t-6 border-brand-orange rounded-full animate-spin bg-gradient-to-r from-brand-orange to-blue-100 shadow-lg"></div>
              </div>
              <div className="mt-2 text-sm font-openSans text-center">
                We are fetching the document details, please wait...
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Name Mismatch Confirmation Modal */}
      <Dialog
        open={showNameMismatchModal}
        onOpenChange={setShowNameMismatchModal}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg text-brand-orange flex items-center gap-2">
              Name Mismatch Detected
            </DialogTitle>
            <DialogDescription className="text-xs lg:text-sm text-gray-600 mt-2">
              {nameMismatchModalType === "both"
                ? "There are name mismatches between your PAN and Aadhaar documents and the Authorized Representative name."
                : `There is a name mismatch between the ${
                    nameMismatchModalType === "pan" ? "PAN" : "Aadhaar"
                  } document and the Authorized Representative name.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Name Comparison */}
            <div className="px-4 py-2 bg-yellow-50 border border-yellow-200 rounded">
              <div className="space-y-1">
                <p className="text-xs lg:text-sm font-semibold text-gray-700">
                  Authorized Representative Name:
                </p>
                <p className="text-xs lg:text-sm text-gray-600">
                  {nameMismatchDetails.profileName}
                </p>

                {/* PAN Details - Show if PAN mismatch or both */}
                {(nameMismatchModalType === "pan" ||
                  nameMismatchModalType === "both") && (
                  <div className="mt-4 pt-4 border-t border-yellow-300">
                    <p className="text-xs lg:text-sm font-semibold text-gray-700">
                      PAN Document Name:
                    </p>
                    <p className="text-xs lg:text-sm text-gray-600">
                      {nameMismatchDetails.panDocumentName || "N/A"}
                    </p>
                  </div>
                )}

                {/* Aadhaar Details - Show if Aadhaar mismatch or both */}
                {(nameMismatchModalType === "aadhaar" ||
                  nameMismatchModalType === "both") && (
                  <div className="mt-4 pt-4 border-t border-yellow-300">
                    <p className="text-xs lg:text-sm font-semibold text-gray-700">
                      Aadhaar Document Name:
                    </p>
                    <p className="text-sm text-gray-600">
                      {nameMismatchDetails.aadhaarDocumentName || "N/A"}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* PAN Confirmation Checkbox - Show if PAN mismatch or both */}
            {(nameMismatchModalType === "pan" ||
              nameMismatchModalType === "both") && (
              <div className="flex items-start gap-3 p-4 bg-gray-50 border border-gray-200 rounded">
                <input
                  type="checkbox"
                  id="pan-name-mismatch-confirm"
                  checked={panNameMismatchConfirmed}
                  onChange={(e) =>
                    setPanNameMismatchConfirmed(e.target.checked)
                  }
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="pan-name-mismatch-confirm"
                  className="text-sm text-gray-700 cursor-pointer"
                >
                  I confirm that I am doing this intentionally for PAN and the
                  document belongs to the Authorized Representative. I
                  understand that the name on the document may differ from the
                  AR name due to legal name changes, marriage, or other valid
                  reasons.
                </label>
              </div>
            )}

            {/* Aadhaar Confirmation Checkbox - Show if Aadhaar mismatch or both */}
            {(nameMismatchModalType === "aadhaar" ||
              nameMismatchModalType === "both") && (
              <div className="flex items-start gap-3 p-4 bg-gray-50 border border-gray-200 rounded">
                <input
                  type="checkbox"
                  id="aadhaar-name-mismatch-confirm"
                  checked={aadhaarNameMismatchConfirmed}
                  onChange={(e) =>
                    setAadhaarNameMismatchConfirmed(e.target.checked)
                  }
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="aadhaar-name-mismatch-confirm"
                  className="text-sm text-gray-700 cursor-pointer"
                >
                  I confirm that I am doing this intentionally for Aadhaar and
                  the document belongs to the Authorized Representative. I
                  understand that the name on the document may differ from the
                  AR name due to legal name changes, marriage, or other valid
                  reasons.
                </label>
              </div>
            )}

            {/* Name Mismatch Reason Input */}
            <div className="space-y-2">
              <Label
                htmlFor="name_mismatch_reason"
                className="text-sm font-semibold text-gray-700"
              >
                Reason for Name Mismatch <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-gray-500">
                Please provide a reason for the name mismatch (e.g., legal name
                change, marriage, etc.)
              </p>
              <Input
                id="name_mismatch_reason"
                type="text"
                placeholder="Enter reason for name mismatch"
                value={nameMismatchReason}
                onChange={(e) => setNameMismatchReason(e.target.value)}
                className="w-full border-[1px] border-brand-borderLightGray py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
                required
              />
            </div>

            {/* Supporting Documents Upload */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-700">
                Upload Supporting Documents (Required)
              </p>
              <p className="text-xs text-gray-500">
                Please upload any supporting documents (e.g., marriage
                certificate, gazette notification, legal name change document,
                etc.) that verify why the name on the document(s) may differ
                from the Authorized Representative name. You can upload any
                document type.
              </p>
              <CustomMultipleFileUploader
                key={renderKey + "-supporting-docs"}
                handleChange={(data: any) => {
                  setSupportingDocuments(data || []);
                }}
                allowedMimeTypes={[
                  "application/pdf",
                  "image/png",
                  "image/jpeg",
                  "image/jpg",
                  "image/gif",
                  "image/webp",
                ]}
                property_uuid={bidderUuid}
                owner_service_type="ar_name_mismatch_supporting"
                data={supportingDocuments}
                maxAllowed={5}
                defaultText="Upload supporting documents (PDF, Images, etc.)"
                ref={supportingDocsUploaderRef}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowNameMismatchModal(false);
                setNameMismatchReason("");
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleNameMismatchConfirm}
              disabled={
                loading ||
                !nameMismatchReason ||
                nameMismatchReason.trim() === "" ||
                (nameMismatchModalType === "pan"
                  ? !panNameMismatchConfirmed ||
                    supportingDocuments.length === 0
                  : nameMismatchModalType === "aadhaar"
                  ? !aadhaarNameMismatchConfirmed ||
                    supportingDocuments.length === 0
                  : // "both" case - both must be confirmed and have supporting docs
                    !panNameMismatchConfirmed ||
                    !aadhaarNameMismatchConfirmed ||
                    supportingDocuments.length === 0)
              }
              className="bg-blue-600 hover:bg-blue-700"
            >
              Confirm & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div>
        {/* AR PAN Upload */}
        <div className="mt-4 text-sm font-semibold">PAN</div>
        <div className="mt-4 max-w-xl">
          {form.getField("is_pan_verified") === true &&
          arPanFiles.length > 0 ? (
            <div className="border rounded p-4 bg-brand-bgLightGrey">
              <div className="flex flex-col items-center mb-3">
                <img
                  src={`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${arPanFiles[0].id}`}
                  alt="Authorized Representative PAN"
                  className="w-auto h-32 object-contain mb-2 border opacity-10"
                />
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <p className="text-sm text-green-500 capitalize font-semibold">
                    PAN is verified successfully
                  </p>
                </div>
                {arPanOCRData?.detections?.[0]?.id_attributes?.name && (
                  <p className="text-xs text-gray-600 mt-1">
                    Name on document:{" "}
                    {arPanOCRData.detections[0].id_attributes.name}
                  </p>
                )}
              </div>
              <div className="flex justify-center mt-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handlePanDocumentRemove}
                  disabled={loading}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  Remove Document
                </Button>
              </div>
            </div>
          ) : (
            <CustomMultipleFileUploader
              key={renderKey + "-ar-pan"}
              handleChange={handlePanDocumentChange}
              allowedMimeTypes={["image/png", "image/jpeg"]}
              property_uuid={bidderUuid}
              owner_service_type="ar_pan"
              data={arPanFiles}
              maxAllowed={1}
              defaultText="Please upload Authorized Representative PAN*"
              ref={arPanUploaderRef}
            />
          )}
          {form.errors.get("is_pan_verified") ? (
            <p className="mt-1 text-sm text-red-500">
              {form.errors.get("is_pan_verified")[0]}
            </p>
          ) : null}
          {panNameMismatchWarning && (
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-xs lg:text-sm text-yellow-800 font-medium">
                ⚠️ Warning: {panNameMismatchWarning}
              </p>
            </div>
          )}
        </div>
        <div className="mt-4 text-sm font-semibold">Aadhaar Front and Back</div>
        {/* AR Aadhaar Front Upload */}
        <div className="mt-4 max-w-xl">
          {form.getField("is_aadhar_front_verified") === true &&
          arAadhaarFrontFiles.length > 0 ? (
            <div className="border rounded p-4 bg-brand-bgLightGrey">
              <div className="flex flex-col items-center mb-3">
                <img
                  src={`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${arAadhaarFrontFiles[0].id}`}
                  alt="Authorized Representative Aadhaar Front"
                  className="w-auto h-32 object-contain mb-2 border opacity-10"
                />
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <p className="text-sm text-green-500 capitalize font-semibold">
                    Aadhaar Front is verified successfully
                  </p>
                </div>
                {arAddharOCRData?.detections?.[0]?.id_attributes?.name && (
                  <p className="text-xs text-gray-600 mt-1">
                    Name on document:{" "}
                    {arAddharOCRData.detections[0].id_attributes.name}
                  </p>
                )}
              </div>
              <div className="flex justify-center mt-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAadhaarFrontRemove}
                  disabled={loading}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  Remove Document
                </Button>
              </div>
            </div>
          ) : (
            <CustomMultipleFileUploader
              key={renderKey + "-ar-aadhaar-front"}
              handleChange={async (data: any) => {
                setArAadhaarFrontFiles(data);
                if (data?.[0]?.id && arAadhaarBackFiles?.[0]?.id) {
                  form.setField("is_both_side_aadhar_uploaded", true);
                  console.log("Both Aadhaar files are uploaded");
                  await handleAadhaarDocumentVerification(
                    data,
                    arAadhaarBackFiles,
                  );
                }
              }}
              allowedMimeTypes={["image/png", "image/jpeg"]}
              property_uuid={bidderUuid}
              owner_service_type="ar_aadhaar-front"
              data={arAadhaarFrontFiles}
              maxAllowed={1}
              defaultText="Please upload Authorized Representative Aadhaar Front*"
              ref={arAadhaarFrontUploaderRef}
            />
          )}
        </div>
        {/* AR Aadhaar Back Upload */}
        <div className="mt-4 max-w-xl">
          {form.getField("is_aadhar_back_verified") === true &&
          arAadhaarBackFiles.length > 0 ? (
            <div className="border rounded p-4 bg-brand-bgLightGrey">
              <div className="flex flex-col items-center mb-3">
                <img
                  src={`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${arAadhaarBackFiles[0].id}`}
                  alt="Authorized Representative Aadhaar Back"
                  className="w-auto h-32 object-contain mb-2 border opacity-10"
                />
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <p className="text-sm text-green-500 capitalize font-semibold">
                    Aadhaar Back is verified successfully
                  </p>
                </div>
                {arAddharOCRData?.detections?.[0]?.id_attributes?.name && (
                  <p className="text-xs text-gray-600 mt-1">
                    Name on document:{" "}
                    {arAddharOCRData.detections[0].id_attributes.name}
                  </p>
                )}
              </div>
              <div className="flex justify-center mt-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAadhaarBackRemove}
                  disabled={loading}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  Remove Document
                </Button>
              </div>
            </div>
          ) : (
            <CustomMultipleFileUploader
              key={renderKey + "-ar-aadhaar-back"}
              handleChange={async (data: any) => {
                setArAadhaarBackFiles(data);
                if (data?.[0]?.id && arAadhaarFrontFiles?.[0]?.id) {
                  form.setField("is_both_side_aadhar_uploaded", true);
                  console.log("Both Aadhaar files are uploaded");
                  await handleAadhaarDocumentVerification(
                    arAadhaarFrontFiles,
                    data,
                  );
                }
              }}
              allowedMimeTypes={["image/png", "image/jpeg"]}
              property_uuid={bidderUuid}
              owner_service_type="ar_aadhaar-back"
              data={arAadhaarBackFiles}
              maxAllowed={1}
              defaultText="Please upload Authorized Representative Aadhaar Back*"
              ref={arAadhaarBackUploaderRef}
            />
          )}

          {form.errors.get("is_aadhar_back_verified") ? (
            <p className="mt-1 text-sm text-red-500">
              {form.errors.get("is_aadhar_back_verified")[0]}
            </p>
          ) : null}
          {aadhaarNameMismatchWarning && (
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-xs lg:text-sm text-yellow-800 font-medium">
                ⚠️ Warning: {aadhaarNameMismatchWarning}
              </p>
            </div>
          )}
        </div>
        {arPanFiles.length > 0 &&
          arAadhaarFrontFiles.length > 0 &&
          arAadhaarBackFiles.length > 0 && (
            <div className="mt-8">
              <div className="flex">
                <Button
                  disabled={loading}
                  className="w-full max-w-xl h-10 text-[18px]"
                  onClick={handleSubmit}
                >
                  {loading ? "Verifying..." : "Next"}
                </Button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};
